/**
 * Le budget à enveloppes : le calcul, et les écritures qui le font vivre.
 *
 * Tout est calculé en local depuis Dexie, jamais demandé au serveur — c'est
 * l'engagement offline-first. Le serveur ne fait que transporter les lignes.
 *
 * LE POINT À NE JAMAIS PERDRE DE VUE : il y a DEUX montants globaux, tous les
 * deux vrais, et qui ne veulent pas dire la même chose.
 *
 *   « en poche »  = revenus réels − dépenses réelles. Vérifiable en recomptant
 *                   ses billets. C'est le seul chiffre que l'utilisateur peut
 *                   contrôler à la main.
 *   « à placer »  = en poche − ce qui reste dans les enveloppes. L'argent qui
 *                   n'a pas encore de destination.
 *
 * Ils partent égaux et divergent dès la première répartition, sans qu'un seul
 * ariary ait bougé. Les confondre à l'écran, c'est soit faire croire à
 * l'utilisateur qu'il est ruiné, soit lui faire dépenser l'argent du loyer.
 */
import {
  db, type Budget, type BudgetAllocation, type Cycle, type Direction, type Goal,
  type GoalDeposit, type PlanMode, type Transaction,
} from './database'
import { rememberCycleIncome } from './marks'
import { deleteLocal, writeLocal } from './sync'
import {
  contains, cycleIncomeLabel, cycleRange, cyclesUpTo, previousCycle, type CycleRange,
} from '../lib/cycles'
import { today } from '../lib/dates'
import { derivedId, newId } from '../lib/uuid'

export interface Envelope {
  budget_id: string
  category_id: string
  /** Affecté pour CE cycle. */
  amount: number
  /** Mis de côté depuis le début. Égal à `amount`, sauf enveloppe cumulative. */
  accumulated: number
  /** Dépensé sur le cycle — ou depuis toujours si l'enveloppe est cumulative. */
  spent: number
  /**
   * La part de `spent` qui n'est venue d'AUCUNE journée prévue : une dépense
   * saisie à la main dans une enveloppe qui, par ailleurs, se planifie.
   *
   * Elle compte dans le solde depuis toujours — c'est bien une dépense — mais
   * rien ne la disait à l'écran : l'utilisateur lisait « 1/8 journées
   * confirmées » et « 127 000 restants » sans pouvoir refaire le calcul, parce
   * qu'il manquait justement ce nombre-là. Un écart qu'on ne peut pas expliquer
   * sur une app d'argent se lit comme une erreur de l'app.
   */
  spentOffPlan: number
  /**
   * Enveloppe de dépense : ce qu'il reste à dépenser, négatif = dépassement.
   * Enveloppe cumulative (épargne) : ce qu'il reste à mettre de côté, négatif =
   * objectif dépassé — ce qui est une bonne nouvelle, pas une faute.
   */
  remaining: number
  /**
   * Ce que l'enveloppe soustrait à l'argent à placer.
   *
   * Enveloppe de dépense : ce qu'il reste à dépenser.
   * Enveloppe cumulative : seulement la part de CE CYCLE pas encore mise de
   * côté. L'argent déjà épargné a quitté la poche, il n'y a rien à en réserver ;
   * et une contribution ratée le mois dernier est une occasion manquée, pas une
   * dette qui gèlerait l'argent d'aujourd'hui.
   */
  reserved: number
  plan_mode: PlanMode
  /** Journées prévues sur le cycle, et celles déjà confirmées (mode « jour par jour »). */
  planned_days: number
  confirmed_days: number
  carries_over: boolean
  /**
   * Vrai seulement pour une enveloppe de DÉPENSE en dépassement. Une épargne
   * qui dépasse son objectif n'est pas en faute : la traiter comme telle
   * reviendrait à gronder l'utilisateur d'avoir trop épargné.
   */
  overspent: boolean
}

/** Un objectif d'épargne, et où il en est. */
export interface GoalProgress {
  goal: Goal
  /** Ce qui est réellement mis de côté : versements moins reprises. */
  saved: number
  /** Ce qu'il reste à mettre. Jamais négatif — un objectif dépassé est atteint. */
  missing: number
  /** De 0 à 1. Vaut 1 dès que la cible est atteinte, même sans cible chiffrée. */
  ratio: number
  reached: boolean
  /**
   * Ce qu'il faudrait mettre par cycle pour tenir l'échéance. `null` sans
   * échéance, ou une fois l'objectif atteint : proposer un rythme pour un
   * objectif déjà rempli n'a aucun sens.
   */
  perCycle: number | null
}

export interface BudgetSummary {
  range: CycleRange
  /** Ce que tu as vraiment. */
  inPocket: number
  /** Ce qu'il te reste à placer. Négatif = plus réparti que ce que tu possèdes. */
  toAllocate: number
  /**
   * Ce qui est entré et ce qui est sorti SUR CE CYCLE — et rien d'autre.
   * `inPocket` traîne tout l'historique derrière lui : sans ces deux nombres,
   * l'utilisateur ne peut pas savoir combien il a touché ce mois-ci, ni d'où
   * vient l'écart entre son revenu et ce qu'il lui reste.
   */
  cycleIncome: number
  cycleExpense: number
  /**
   * Ce qui restait des cycles PRÉCÉDENTS, et qui est toujours en poche.
   *
   * Purement dérivé — `inPocket − cycleIncome + cycleExpense` — et jamais
   * écrit : cet argent est déjà dans `inPocket`, puisqu'il vient de revenus
   * passés non dépensés. En faire une transaction le compterait DEUX FOIS et
   * ferait afficher un solde que l'utilisateur ne peut plus vérifier en
   * recomptant ses billets (invariants 1 et 5).
   *
   * Il existe parce que sans lui le compte ne tombe pas juste à l'écran :
   * « en poche 390 750 » sous « revenus du mois +300 000, dépenses 0 » se lit
   * comme une erreur de l'app. Négatif = les cycles passés ont laissé un trou.
   */
  broughtForward: number
  envelopes: Envelope[]
  /** Dépensé ce cycle dans une catégorie sans enveloppe. */
  outOfEnvelope: number
  /**
   * Les objectifs d'épargne et leur avancement.
   *
   * ⚠️ Ce qu'ils retiennent s'ajoute à ce que les enveloppes gèlent, et sort
   * donc de « à placer » — jamais de « en poche ». Aucun ariary ne bouge : un
   * versement est une INTENTION, pas une dépense (invariant 3).
   */
  goals: GoalProgress[]
  /** Ce que les objectifs retiennent au total. */
  goalsSaved: number
}

const kept = <T extends { deleted_at: string | null }>(rows: T[]) =>
  rows.filter((row) => !row.deleted_at)

const total = (rows: Array<{ amount: number }>) =>
  rows.reduce((sum, row) => sum + row.amount, 0)

/**
 * L'enveloppe qui s'applique à un cycle : la version la plus récente parmi
 * celles qui ont déjà pris effet.
 *
 * C'est ici que se joue « une enveloppe passée ne se réécrit jamais » : passer
 * nourriture de 100 000 à 120 000 en octobre insère une ligne datée du
 * 1er octobre, et septembre continue de lire la sienne, à 100 000.
 */
export function applicableBudget(
  budgets: Budget[], categoryId: string, cycleStart: string,
): Budget | undefined {
  return kept(budgets)
    .filter((b) => b.category_id === categoryId && b.effective_from <= cycleStart)
    .sort((a, b) => b.effective_from.localeCompare(a.effective_from))[0]
}

/**
 * Le calcul, isolé de Dexie pour être testable sur des données posées à la main.
 */
export function computeSummary(input: {
  cycle: Cycle
  date: string
  transactions: Transaction[]
  budgets: Budget[]
  allocations?: BudgetAllocation[]
  goals?: Goal[]
  goalDeposits?: GoalDeposit[]
}): BudgetSummary {
  const { cycle, date } = input
  const range = cycleRange(date, cycle)
  const transactions = kept(input.transactions)
  const budgets = kept(input.budgets)

  const expenses = transactions.filter((t) => t.direction === 'expense')
  const incomes = transactions.filter((t) => t.direction === 'income')
  const planned = kept(input.allocations ?? [])
    .filter((a) => contains(range, a.planned_date))

  // Toutes les catégories qui ont une enveloppe en vigueur ce cycle.
  const categories = [...new Set(budgets.map((b) => b.category_id))]
  const envelopes: Envelope[] = []

  for (const category_id of categories) {
    const budget = applicableBudget(budgets, category_id, range.start)
    if (!budget) continue

    const inCategory = expenses.filter((t) => t.category_id === category_id)

    let accumulated = budget.amount
    let spent: number

    if (budget.carries_over) {
      // L'épargne. Son reste ne retombe jamais dans « à placer » : on cumule ce
      // qui a été mis de côté à chaque cycle depuis le premier, et on en retire
      // tout ce qui en a été repris — depuis toujours, pas seulement ce cycle.
      const first = budgets
        .filter((b) => b.category_id === category_id)
        .reduce((min, b) => (b.effective_from < min ? b.effective_from : min), range.start)

      accumulated = cyclesUpTo(first, range.start, cycle).reduce(
        (sum, c) => sum + (applicableBudget(budgets, category_id, c.start)?.amount ?? 0), 0,
      )
      // Ce qui est réellement mis de côté = les dépôts MOINS les retraits. Un
      // retrait est un revenu dans la catégorie : l'argent revient dans la
      // poche et redevient disponible à répartir. C'est le seul mouvement
      // possible depuis l'épargne, et c'est voulu — on ne dépense pas « depuis
      // l'épargne », on la ramène d'abord dans l'argent à placer.
      const deposits = total(inCategory.filter((t) => t.operation_date <= range.end))
      const withdrawals = total(
        transactions.filter((t) => t.direction === 'income' &&
          t.category_id === category_id && t.operation_date <= range.end))
      spent = deposits - withdrawals
    } else {
      spent = total(inCategory.filter((t) => contains(range, t.operation_date)))
    }

    const days = planned.filter((a) => a.category_id === category_id)
    const remaining = accumulated - spent

    /*
     * Ce qui a été dépensé HORS des journées prévues.
     *
     * On part des TRANSACTIONS et non du montant des allocations : corriger une
     * dépense après coup change la transaction, jamais le `planned_amount` de
     * la journée qui l'a produite. Comparer les deux ferait apparaître un
     * « hors plan » fantôme à chaque correction de montant.
     */
    const fromPlan = new Set(
      days.map((a) => a.transaction_id).filter((id): id is string => id !== null),
    )
    const spentOffPlan = total(
      inCategory.filter((t) => contains(range, t.operation_date) && !fromPlan.has(t.id)),
    )

    let reserved: number
    if (budget.carries_over) {
      const depotsDuCycle =
        total(inCategory.filter((t) => contains(range, t.operation_date))) -
        total(transactions.filter((t) => t.direction === 'income' &&
          t.category_id === category_id && contains(range, t.operation_date)))
      reserved = Math.max(0, budget.amount - Math.max(0, depotsDuCycle))
    } else {
      // Une enveloppe dépassée ne réserve plus rien — elle ne réserve pas
      // « moins que rien ».
      reserved = Math.max(0, remaining)
    }

    envelopes.push({
      budget_id: budget.id,
      category_id,
      amount: budget.amount,
      accumulated,
      spent,
      spentOffPlan,
      remaining,
      reserved,
      // `?? 'none'` : une enveloppe écrite avant l'existence du champ ne le
      // porte pas du tout, elle vaut `undefined` et non la valeur par défaut.
      plan_mode: budget.plan_mode ?? 'none',
      planned_days: days.length,
      confirmed_days: days.filter((a) => a.confirmed_at).length,
      carries_over: budget.carries_over,
      overspent: remaining < 0 && !budget.carries_over,
    })
  }

  const inPocket = total(incomes) - total(expenses)
  // Volontairement sans exception pour l'épargne : un dépôt sort vraiment de la
  // poche, un retrait y revient vraiment. Retrancher ces mouvements casserait
  // la seule vérification que l'utilisateur peut faire de tête —
  // « en poche = ce qu'il restait + entrées − sorties ».
  const cycleIncome = total(incomes.filter((t) => contains(range, t.operation_date)))
  const cycleExpense = total(expenses.filter((t) => contains(range, t.operation_date)))
  // Chaque enveloppe dit elle-même ce qu'elle gèle : une enveloppe de dépense
  // gèle ce qu'il lui reste, une épargne seulement sa contribution du cycle.
  const reserved = envelopes.reduce((sum, e) => sum + e.reserved, 0)

  /*
   * Les objectifs d'épargne.
   *
   * Calculés APRÈS les enveloppes et ajoutés à ce qui est réservé : ils
   * retiennent de l'argent qui reste bel et bien en poche. Les versements ne
   * sont PAS bornés au cycle — un objectif se remplit sur des mois, et
   * n'additionner que le cycle courant afficherait une cagnotte qui se vide
   * toute seule au 1er du mois.
   */
  const deposits = kept(input.goalDeposits ?? []).filter((d) => d.moved_on <= range.end)
  const goals: GoalProgress[] = kept(input.goals ?? [])
    .filter((g) => !g.archived_at)
    .sort((a, b) => a.position - b.position || a.name.localeCompare(b.name))
    .map((goal) => {
      const mine = deposits.filter((d) => d.goal_id === goal.id)
      const saved = total(mine.filter((d) => d.direction === 'deposit'))
        - total(mine.filter((d) => d.direction === 'withdrawal'))
      // `max(0, …)` : une reprise de plus que ce qui a été mis ne doit pas
      // rendre de l'argent qui n'a jamais été réservé.
      const net = Math.max(0, saved)
      const reached = goal.target_amount > 0 && net >= goal.target_amount
      return {
        goal,
        saved: net,
        missing: Math.max(0, goal.target_amount - net),
        ratio: goal.target_amount > 0 ? Math.min(1, net / goal.target_amount) : 0,
        reached,
        perCycle: paceFor(goal, Math.max(0, goal.target_amount - net), date, cycle, reached),
      }
    })
  const goalsSaved = goals.reduce((sum, g) => sum + g.saved, 0)

  const budgeted = new Set(envelopes.map((e) => e.category_id))
  const outOfEnvelope = total(
    expenses.filter((t) =>
      contains(range, t.operation_date) &&
      (!t.category_id || !budgeted.has(t.category_id))),
  )

  return {
    range,
    inPocket,
    // Définition volontaire : « à placer » se déduit de ce qu'on a VRAIMENT,
    // pas des revenus du cycle. C'est ce qui fait remonter tout seul le reste
    // d'un cycle sur le suivant, sans jamais inventer un revenu qui n'existe
    // pas — un faux revenu compterait deux fois le même argent.
    // Les objectifs retiennent en plus des enveloppes : c'est le même argent en
    // poche, mais il a désormais une destination.
    toAllocate: inPocket - reserved - goalsSaved,
    cycleIncome,
    cycleExpense,
    broughtForward: inPocket - cycleIncome + cycleExpense,
    envelopes,
    outOfEnvelope,
    goals,
    goalsSaved,
  }
}

/**
 * Ce qu'il faudrait mettre par cycle pour tenir l'échéance.
 *
 * Le nombre de cycles restants, jamais de jours : l'utilisateur met de côté au
 * rythme où l'argent lui arrive, et « 1 240 Ar par jour » ne se rapporte à
 * aucun geste qu'il puisse faire.
 */
function paceFor(
  goal: Goal, missing: number, date: string, cycle: Cycle, reached: boolean,
): number | null {
  if (reached || missing <= 0 || !goal.target_date) return null
  // L'échéance est passée : le rythme n'a plus de sens, il reste un manque.
  if (goal.target_date <= date) return null

  const cycles = cyclesUpTo(date, goal.target_date, cycle).length
  return cycles > 0 ? Math.ceil(missing / cycles) : missing
}

/** La même chose, lue depuis la base locale. */
export async function loadSummary(
  walletId: string, date = today(),
): Promise<BudgetSummary> {
  const [wallet, transactions, budgets, allocations, goals, goalDeposits] = await Promise.all([
    db.wallets.get(walletId),
    db.transactions.where({ wallet_id: walletId }).toArray(),
    db.budgets.where({ wallet_id: walletId }).toArray(),
    db.budget_allocations.where({ wallet_id: walletId }).toArray(),
    db.goals.where({ wallet_id: walletId }).toArray(),
    db.goal_deposits.where({ wallet_id: walletId }).toArray(),
  ])
  return computeSummary({
    cycle: wallet?.cycle ?? 'monthly', date, transactions, budgets, allocations,
    goals, goalDeposits,
  })
}

/* ─────────────────────────────────────────────────────────────────────────
   Le résumé du cycle — feature 3
   ───────────────────────────────────────────────────────────────────────── */

/** Ce qu'une catégorie a pesé sur un cycle. */
export interface CycleLine {
  category_id: string | null
  name: string
  amount: number
  /** Part du total des dépenses, de 0 à 1. Sert à ordonner, pas à décorer. */
  share: number
  /** Le budget de l'enveloppe, s'il y en avait un. */
  budgeted: number | null
}

export interface CycleReport {
  range: CycleRange
  cycle: Cycle
  income: number
  expense: number
  /** Ce qui reste du cycle : entrées moins sorties. Négatif = on a puisé. */
  net: number
  /** Les dépenses par catégorie, de la plus lourde à la plus légère. */
  lines: CycleLine[]
  /** Le même cycle, un cran plus tôt — pour dire si ça monte ou si ça baisse. */
  previousExpense: number
  previousIncome: number
  /** Combien de jours du cycle portent au moins une dépense. */
  activeDays: number
  /**
   * Ce qui a été mis de côté pendant ce cycle, sur des objectifs **encore
   * vivants**.
   *
   * ⚠️ Un objectif rangé rend son argent : il redevient à placer, donc il n'est
   * plus « mis de côté ». Compter ses versements ici annonçait de l'argent
   * réservé que le budget, lui, montrait comme disponible — deux écrans qui
   * parlent du même argent sans dire la même chose, ce qui est la pire classe
   * de bug de cette app.
   */
  savedOnGoals: number
}

/**
 * Le bilan d'un cycle — feature 3.
 *
 * Volontairement un CALCUL et non un écran de plus dans la navigation : celle-ci
 * est arrêtée à quatre destinations (décision fermée), et le résumé se lit là
 * où l'on se pose la question — sur le budget du cycle qu'on vient de vivre.
 *
 * La comparaison avec le cycle précédent est le cœur : « 320 000 dépensés » ne
 * dit rien tout seul. « 320 000, soit 40 000 de plus que le mois dernier » dit
 * quelque chose sur lequel on peut agir.
 */
export async function cycleReport(
  walletId: string, date = today(),
): Promise<CycleReport> {
  const [wallet, allTransactions, allBudgets, categories, deposits, goals] =
    await Promise.all([
      db.wallets.get(walletId),
      db.transactions.where({ wallet_id: walletId }).toArray(),
      db.budgets.where({ wallet_id: walletId }).toArray(),
      db.categories.where({ wallet_id: walletId }).toArray(),
      db.goal_deposits.where({ wallet_id: walletId }).toArray(),
      // Les objectifs, pour savoir lesquels retiennent ENCORE de l'argent.
      db.goals.where({ wallet_id: walletId }).toArray(),
    ])

  const cycle = wallet?.cycle ?? 'monthly'
  const range = cycleRange(date, cycle)
  const previous = previousCycle(range, cycle)
  const transactions = kept(allTransactions)
  const budgets = kept(allBudgets)
  const nameOf = new Map(categories.map((c) => [c.id, c.name]))

  const inRange = (r: CycleRange) => transactions.filter((t) => contains(r, t.operation_date))
  const sum = (rows: Transaction[], direction: Direction) =>
    total(rows.filter((t) => t.direction === direction))

  const courant = inRange(range)
  const income = sum(courant, 'income')
  const expense = sum(courant, 'expense')

  // Groupées par catégorie, la plus lourde d'abord : c'est l'ordre dans lequel
  // on cherche où l'argent est parti.
  const parCategorie = new Map<string | null, number>()
  for (const t of courant.filter((t) => t.direction === 'expense')) {
    parCategorie.set(t.category_id, (parCategorie.get(t.category_id) ?? 0) + t.amount)
  }

  const lines: CycleLine[] = [...parCategorie.entries()]
    .map(([category_id, amount]) => ({
      category_id,
      name: (category_id && nameOf.get(category_id)) || 'Sans catégorie',
      amount,
      share: expense > 0 ? amount / expense : 0,
      budgeted: category_id
        ? applicableBudget(budgets, category_id, range.start)?.amount ?? null
        : null,
    }))
    .sort((a, b) => b.amount - a.amount)

  const vivants = new Set(
    kept(goals).filter((g) => !g.archived_at).map((g) => g.id),
  )

  const precedent = inRange(previous)
  const jours = new Set(
    courant.filter((t) => t.direction === 'expense').map((t) => t.operation_date))

  return {
    range,
    cycle,
    income,
    expense,
    net: income - expense,
    lines,
    previousExpense: sum(precedent, 'expense'),
    previousIncome: sum(precedent, 'income'),
    activeDays: jours.size,
    // Le MÊME filtre que `computeSummary` : un objectif rangé ou supprimé ne
    // retient plus rien, et son argent est redevenu à placer. Deux calculs qui
    // divergent sur ce point font mentir l'un des deux écrans.
    savedOnGoals: kept(deposits)
      .filter((d) => contains(range, d.moved_on) && vivants.has(d.goal_id))
      .reduce((s, d) => s + (d.direction === 'deposit' ? d.amount : -d.amount), 0),
  }
}

/* ─────────────────────────────────────────────────────────────────────────
   Les objectifs d'épargne — écritures
   ───────────────────────────────────────────────────────────────────────── */

/** Poser ou corriger un objectif. */
export async function saveGoal(input: {
  walletId: string
  id?: string
  name: string
  targetAmount: number
  targetDate?: string | null
  icon?: string | null
  position?: number
}): Promise<string> {
  const id = input.id ?? newId()
  const existing = input.id ? await db.goals.get(input.id) : undefined

  await writeLocal('goals', {
    id,
    wallet_id: input.walletId,
    name: input.name.trim(),
    target_amount: Math.max(0, Math.trunc(input.targetAmount)),
    target_date: input.targetDate?.trim() || null,
    icon: input.icon ?? existing?.icon ?? null,
    position: input.position ?? existing?.position ?? Date.now(),
    // On ne réécrit pas ce qui a été constaté : corriger la cible d'un objectif
    // atteint ne doit pas rejouer la félicitation.
    achieved_at: existing?.achieved_at ?? null,
    archived_at: existing?.archived_at ?? null,
  })
  return id
}

/** Mettre de côté, ou reprendre. Aucune transaction n'est écrite : rien ne bouge. */
export async function moveToGoal(input: {
  walletId: string
  goalId: string
  amount: number
  direction: 'deposit' | 'withdrawal'
  note?: string | null
}): Promise<void> {
  if (input.amount <= 0) return

  await writeLocal('goal_deposits', {
    id: newId(),
    wallet_id: input.walletId,
    goal_id: input.goalId,
    amount: Math.trunc(input.amount),
    direction: input.direction,
    moved_on: today(),
    note: input.note?.trim() || null,
  })
  await markAchieved(input.walletId, input.goalId)
}

/**
 * Constate qu'un objectif vient d'être atteint.
 *
 * Retenu en base plutôt que recalculé à l'affichage : sinon la félicitation se
 * rejouerait à chaque ouverture de l'écran, ce qui la vide de son sens. Et on
 * ne l'efface JAMAIS si l'objectif redescend — il a bel et bien été atteint.
 */
async function markAchieved(walletId: string, goalId: string): Promise<void> {
  const goal = await db.goals.get(goalId)
  if (!goal || goal.achieved_at || goal.target_amount <= 0) return

  const summary = await loadSummary(walletId)
  const progress = summary.goals.find((g) => g.goal.id === goalId)
  if (!progress?.reached) return

  await writeLocal('goals', { ...goal, achieved_at: new Date().toISOString() })
}

/**
 * Ranger un objectif sans perdre son histoire.
 *
 * Archiver plutôt que supprimer : les versements décrivent des mois de mise de
 * côté, et les effacer ferait disparaître ce que l'utilisateur a réellement
 * accompli. L'argent, lui, redevient à placer.
 */
export async function archiveGoal(goalId: string): Promise<void> {
  const goal = await db.goals.get(goalId)
  if (!goal) return
  await writeLocal('goals', { ...goal, archived_at: new Date().toISOString() })
}

/**
 * Pose ou corrige l'enveloppe d'une catégorie pour un cycle.
 *
 * Corriger le cycle EN COURS modifie sa ligne : un cycle en cours n'est pas
 * encore de l'histoire. Viser un cycle futur en insère une nouvelle, et les
 * cycles déjà passés gardent la leur, intacte.
 */
export async function setEnvelope(input: {
  walletId: string
  categoryId: string
  amount: number
  cycleStart: string
  planMode?: PlanMode
  carriesOver?: boolean
}): Promise<void> {
  const existing = (await db.budgets
    .where('[wallet_id+category_id]')
    .equals([input.walletId, input.categoryId])
    .toArray())
    .find((b) => !b.deleted_at && b.effective_from === input.cycleStart)

  const plan_mode = input.planMode ?? existing?.plan_mode ?? 'none'

  await writeLocal('budgets', {
    id: existing?.id ?? newId(),
    wallet_id: input.walletId,
    category_id: input.categoryId,
    amount: input.amount,
    effective_from: input.cycleStart,
    plan_mode,
    carries_over: input.carriesOver ?? existing?.carries_over ?? false,
  })

  // L'échéance unique se maintient ICI, et pas dans l'écran : c'est la seule
  // porte par laquelle une enveloppe s'écrit, donc le seul endroit où l'on est
  // sûr de rattraper aussi bien le changement de mode que le changement de
  // montant. La laisser à l'appelant, c'est se retrouver un jour avec une
  // échéance de 200 000 sur une enveloppe passée à 150 000.
  await syncSingleDueDate({
    walletId: input.walletId,
    categoryId: input.categoryId,
    cycleStart: input.cycleStart,
    mode: plan_mode,
    amount: input.amount,
  })
}

/**
 * Tient à jour l'échéance unique du mode « en une fois ».
 *
 * Une ligne à confirmer, datée d'aujourd'hui si le cycle est en cours, du
 * premier jour sinon. Les lignes DÉJÀ CONFIRMÉES ne sont jamais touchées :
 * elles décrivent de l'argent réellement sorti.
 */
async function syncSingleDueDate(input: {
  walletId: string
  categoryId: string
  cycleStart: string
  mode: PlanMode
  amount: number
}): Promise<void> {
  const wallet = await db.wallets.get(input.walletId)
  const range = cycleRange(input.cycleStart, wallet?.cycle ?? 'monthly')
  const existing = await allocationsForEnvelope(input.walletId, input.categoryId, range)
  const pending = existing.filter((a) => !a.confirmed_at)

  if (input.mode !== 'once') {
    // On ne nettoie que si l'on quitte « en une fois » : passer en « jour par
    // jour » laisse la planification faire son propre ménage.
    if (input.mode === 'none') {
      for (const orphan of pending) await deleteLocal('budget_allocations', orphan.id)
    }
    return
  }

  const paid = existing
    .filter((a) => a.confirmed_at)
    .reduce((sum, a) => sum + a.planned_amount, 0)
  const due = Math.max(0, input.amount - paid)
  const date = contains(range, today()) ? today() : range.start

  // Une seule échéance vivante : les éventuelles autres disparaissent.
  const [keep, ...extra] = pending
  for (const orphan of extra) await deleteLocal('budget_allocations', orphan.id)

  if (due === 0) {
    if (keep) await deleteLocal('budget_allocations', keep.id)
    return
  }

  await writeLocal('budget_allocations', {
    id: keep?.id ?? newId(),
    wallet_id: input.walletId,
    category_id: input.categoryId,
    planned_date: date,
    planned_amount: due,
    label: keep?.label ?? null,
    confirmed_at: null,
    transaction_id: null,
  })
}

/**
 * Confirme une ligne prévue — le geste quotidien de l'app.
 *
 * `amount` peut différer du prévu : l'utilisateur ajuste puis confirme, c'est le
 * cas courant. La dépense réelle est écrite AVANT le marquage de la ligne : si
 * l'app est tuée entre les deux, il reste une dépense enregistrée sans sa
 * confirmation — visible et rattrapable. L'ordre inverse marquerait la ligne
 * confirmée sans qu'aucun argent ne soit sorti, et sous-estimerait les dépenses
 * en silence.
 */
export async function confirmAllocation(
  allocation: BudgetAllocation, amount: number,
): Promise<void> {
  // Confirmer à zéro, c'est dire « cette journée ne m'a rien coûté ». On marque
  // la ligne et on n'écrit AUCUNE transaction : une dépense de 0 Ar dans
  // l'historique n'apprend rien et encombre la lecture. C'est le cas courant à
  // la clôture, où l'utilisateur solde toutes ses journées d'un coup.
  if (amount <= 0) {
    await writeLocal('budget_allocations', {
      ...allocation,
      planned_amount: 0,
      confirmed_at: new Date().toISOString(),
      transaction_id: null,
    })
    return
  }

  /*
   * Un identifiant CALCULÉ à partir de l'allocation, jamais tiré au sort.
   *
   * Une journée prévue ne peut produire qu'UNE dépense. Deux appareils hors
   * ligne qui confirment la même journée fabriquaient chacun un identifiant
   * différent : le serveur recevait deux transactions, les acceptait toutes les
   * deux, et la dépense était comptée en double — sans erreur, et sans qu'un
   * écran de conflit puisse le voir. Avec un identifiant dérivé, ils écrivent
   * la même ligne et le second envoi ÉCRASE au lieu de dupliquer.
   *
   * `?? ` conservé pour les journées déjà confirmées avant cette décision :
   * leur dépense garde son ancien identifiant, et rien ne se dédouble.
   */
  const transactionId = allocation.transaction_id ?? await derivedId(allocation.id)

  await writeLocal('transactions', {
    id: transactionId,
    wallet_id: allocation.wallet_id,
    category_id: allocation.category_id,
    entered_by_account_id: null,
    amount,
    direction: 'expense' as const,
    operation_date: allocation.planned_date,
    // Ce que l'utilisateur a écrit en planifiant sa journée nomme la ligne.
    title: allocation.label,
    note: null,
  })

  await writeLocal('budget_allocations', {
    ...allocation,
    planned_amount: amount,
    confirmed_at: new Date().toISOString(),
    transaction_id: transactionId,
  })
}

/* ─────────────────────────────────────────────────────────────────────────
   Planifier ses journées
   ───────────────────────────────────────────────────────────────────────── */

/** Le prévisionnel d'une enveloppe sur une période, confirmé ou non. */
export async function allocationsForEnvelope(
  walletId: string, categoryId: string, range: CycleRange,
): Promise<BudgetAllocation[]> {
  const rows = await db.budget_allocations
    .where('[wallet_id+category_id]')
    .equals([walletId, categoryId])
    .toArray()
  return kept(rows)
    .filter((a) => contains(range, a.planned_date))
    .sort((a, b) => a.planned_date.localeCompare(b.planned_date))
}

/** Une journée de mouvements dans une enveloppe, et le détail de ses lignes. */
export interface EnvelopeDay {
  date: string
  /**
   * Le mouvement NET du jour : dépenses moins reprises. Positif = de l'argent
   * est sorti de l'enveloppe.
   *
   * Le net et non la seule somme des dépenses : une enveloppe cumulative reçoit
   * des dépôts (des dépenses) et rend des retraits (des revenus dans la même
   * catégorie), et n'additionner que les premiers annoncerait une journée bien
   * plus coûteuse qu'elle ne l'a été.
   */
  net: number
  /** Les lignes de la journée, dans l'ordre où elles ont été saisies. */
  transactions: Transaction[]
  /** Celles qui ne viennent d'aucune journée prévue — saisies à la main. */
  offPlan: Set<string>
}

/**
 * Tout ce qui a bougé dans une enveloppe sur un cycle, jour par jour.
 *
 * On part des transactions et non des allocations : une journée peut porter
 * plusieurs lignes — celle qu'on avait prévue, plus celles qu'on ajoute au
 * marché — et n'afficher que le montant d'une allocation cacherait les
 * secondes. C'est précisément ce qui rendait le compte de l'enveloppe
 * indéchiffrable.
 *
 * Vaut pour TOUTE enveloppe, planifiée ou non : « où est passé l'argent de
 * cette enveloppe » est la même question que le mode de dépense soit « jour par
 * jour », « en une fois » ou « au fil de l'eau ».
 */
export async function envelopeHistory(
  walletId: string, categoryId: string, range: CycleRange,
): Promise<EnvelopeDay[]> {
  const [transactions, allocations] = await Promise.all([
    db.transactions.where({ wallet_id: walletId }).toArray(),
    db.budget_allocations.where('[wallet_id+category_id]')
      .equals([walletId, categoryId]).toArray(),
  ])

  const fromPlan = new Set(
    kept(allocations)
      .map((a) => a.transaction_id)
      .filter((id): id is string => id !== null),
  )

  const byDate = new Map<string, Transaction[]>()
  for (const t of kept(transactions)) {
    if (t.category_id !== categoryId) continue
    if (!contains(range, t.operation_date)) continue
    byDate.set(t.operation_date, (byDate.get(t.operation_date) ?? []).concat(t))
  }

  return [...byDate.entries()]
    .map(([date, rows]) => ({
      date,
      net: total(rows.filter((t) => t.direction === 'expense'))
        - total(rows.filter((t) => t.direction === 'income')),
      // Trié sur l'IDENTIFIANT : les UUIDv7 portent l'horodatage de création
      // dans leurs premiers bits, donc l'ordre lexicographique EST l'ordre de
      // saisie — et il ne bouge plus quand on corrige un montant.
      transactions: [...rows].sort((a, b) => a.id.localeCompare(b.id)),
      offPlan: new Set(rows.filter((t) => !fromPlan.has(t.id)).map((t) => t.id)),
    }))
    .sort((a, b) => a.date.localeCompare(b.date))
}

/**
 * Pose le prévu d'une enveloppe sur une liste de dates.
 *
 * Replanifier RÉUTILISE la ligne existante d'un jour au lieu d'en créer une
 * seconde : sans ça, ajuster son plan doublerait le prévu de chaque journée.
 * Les jours retirés du plan sont supprimés — sauf ceux déjà confirmés, qui
 * décrivent de l'argent réellement dépensé et n'appartiennent plus au plan.
 */
export async function planDays(input: {
  walletId: string
  categoryId: string
  range: CycleRange
  days: Array<{ date: string; amount: number }>
  label?: string | null
}): Promise<void> {
  const existing = await allocationsForEnvelope(input.walletId, input.categoryId, input.range)
  const byDate = new Map(existing.map((a) => [a.planned_date, a]))
  const wanted = new Set(input.days.map((d) => d.date))

  for (const day of input.days) {
    const current = byDate.get(day.date)
    if (current?.confirmed_at) continue          // déjà dépensé : on n'y touche pas
    await writeLocal('budget_allocations', {
      id: current?.id ?? newId(),
      wallet_id: input.walletId,
      category_id: input.categoryId,
      planned_date: day.date,
      planned_amount: day.amount,
      label: input.label ?? current?.label ?? null,
      confirmed_at: null,
      transaction_id: null,
    })
  }

  for (const orphan of existing) {
    if (wanted.has(orphan.planned_date) || orphan.confirmed_at) continue
    await deleteLocal('budget_allocations', orphan.id)
  }
}

/* ─────────────────────────────────────────────────────────────────────────
   La clôture de cycle
   ───────────────────────────────────────────────────────────────────────── */

export interface CycleClose {
  /** Le cycle qui vient de s'achever. */
  range: CycleRange
  /** Celui qui commence. */
  next: CycleRange
  received: number
  spent: number
  /** Journées prévues et jamais confirmées. Dernière occasion de le dire. */
  unconfirmed: BudgetAllocation[]
  /**
   * Enveloppes « au fil de l'eau » qui gardent un reste. Rien n'y était prévu,
   * donc rien ne dit si l'argent a été dépensé : il faut poser la question.
   */
  atLarge: Envelope[]
  /** Toutes les enveloppes non cumulatives qui gardent un reste. */
  leftovers: Envelope[]
  leftoverTotal: number
  savingsCategoryId: string | null
  expectedIncome: number | null
  /**
   * Le rythme du cycle qu'on solde. Sans lui la feuille de clôture parlait de
   * « août 2026 » à quelqu'un qui budgète à la semaine, et lui reproposait
   * « chaque mois » coché par défaut à chaque fin de semaine.
   */
  cycle: Cycle
}

/** Ce que l'utilisateur répond au fil de la clôture. */
export interface CloseAnswers {
  /** Ce qui a réellement été dépensé dans les enveloppes « au fil de l'eau ». */
  spentByCategory?: Record<string, number>
  /** Que faire de ce qui reste au bout du compte. */
  leftover?: 'savings' | 'available' | 'spent'
  /** Le revenu réellement reçu pour le nouveau cycle. `null` = pas maintenant. */
  income?: number | null
  /** Le rythme du nouveau cycle — il peut changer d'un mois sur l'autre. */
  cycle?: Cycle
  /** Reconduire les enveloppes du cycle écoulé, ou repartir de zéro. */
  budgets?: 'keep' | 'reset'
}

const closeKey = (range: CycleRange) => `cycle_reviewed:${range.start}`

/**
 * Le cycle précédent est-il à clôturer ?
 *
 * On ne demande rien sur un cycle que l'utilisateur n'a pas vécu dans l'app :
 * un nouveau venu serait accueilli par un bilan d'un mois qu'il n'a jamais saisi.
 */
export async function pendingClose(
  walletId: string, date = today(),
): Promise<CycleClose | null> {
  const wallet = await db.wallets.get(walletId)
  const cycle = wallet?.cycle ?? 'monthly'
  const courant = cycleRange(date, cycle)

  const transactions = kept(await db.transactions.where({ wallet_id: walletId }).toArray())

  /*
   * LE DERNIER CYCLE VÉCU, et non le cycle précédent.
   *
   * Quelqu'un qui rouvre l'app après trois mois d'absence n'a rien à solder sur
   * le mois d'avant : il est vide. On remonte donc à la dernière opération
   * antérieure au cycle en cours, et c'est SON cycle qu'on propose de clore.
   * Sans ça, le seul cycle réellement vécu n'était jamais soldé, et ses
   * enveloppes continuaient de geler de l'argent indéfiniment.
   *
   * Un seul cycle, jamais une file : enchaîner cinq clôtures à quelqu'un qui
   * revient après six mois est le meilleur moyen de le faire répondre au
   * hasard. Le cycle qui redémarre est celui d'AUJOURD'HUI (`next`), pas le
   * suivant de celui qu'on clôt.
   */
  const derniere = transactions
    .filter((t) => t.operation_date < courant.start)
    .reduce((max, t) => (t.operation_date > max ? t.operation_date : max), '')
  if (!derniere) return null

  const range = cycleRange(derniere, cycle)
  if (await db.meta.get(closeKey(range))) return null

  const dansLeCycle = transactions.filter((t) => contains(range, t.operation_date))
  if (dansLeCycle.length === 0) return null

  const budgets = await db.budgets.where({ wallet_id: walletId }).toArray()
  const allocations = kept(
    await db.budget_allocations.where({ wallet_id: walletId }).toArray())

  const summary = computeSummary({
    cycle, date: range.end, transactions, budgets, allocations,
  })
  const leftovers = summary.envelopes.filter((e) => !e.carries_over && e.remaining > 0)

  const next = courant
  const alreadyEarned = transactions.some(
    (t) => t.direction === 'income' && contains(next, t.operation_date))
  const expected = wallet?.expected_income ?? 0

  const current = computeSummary({ cycle, date, transactions, budgets, allocations })

  return {
    range,
    next,
    received: dansLeCycle.filter((t) => t.direction === 'income')
      .reduce((sum, t) => sum + t.amount, 0),
    spent: dansLeCycle.filter((t) => t.direction === 'expense')
      .reduce((sum, t) => sum + t.amount, 0),
    unconfirmed: allocations
      .filter((a) => !a.confirmed_at && contains(range, a.planned_date))
      .sort((a, b) => a.planned_date.localeCompare(b.planned_date)),
    atLarge: leftovers.filter((e) => e.plan_mode === 'none'),
    leftovers,
    leftoverTotal: leftovers.reduce((sum, e) => sum + e.remaining, 0),
    savingsCategoryId:
      current.envelopes.find((e) => e.carries_over)?.category_id ?? null,
    expectedIncome: expected > 0 && !alreadyEarned ? expected : null,
    cycle,
  }
}

/**
 * Clôt le cycle et ouvre le suivant.
 *
 * L'ordre compte : on corrige d'abord ce qui a été dépensé sans être noté, puis
 * seulement on décide du reste — sinon on déciderait du sort d'un argent qui
 * n'existe déjà plus.
 */
export async function closeCycle(
  walletId: string, close: CycleClose, answers: CloseAnswers = {},
): Promise<void> {
  const spentByCategory = answers.spentByCategory ?? {}

  // 1. Les enveloppes « au fil de l'eau » que l'utilisateur dit avoir dépensées.
  let corrected = 0
  for (const [category_id, amount] of Object.entries(spentByCategory)) {
    if (amount <= 0) continue
    corrected += amount
    // Une correction PAR ENVELOPPE et par cycle clos : deux appareils qui
    // soldent le même cycle décrivent la même dépense, pas deux.
    await writeLocal('transactions', {
      id: await derivedId(`spent:${walletId}:${close.range.start}:${category_id}`),
      wallet_id: walletId,
      category_id,
      entered_by_account_id: null,
      amount,
      direction: 'expense' as const,
      operation_date: close.range.end,
      title: 'Dépense non détaillée',
      note: null,
    })
  }

  // 3. Le sort du reste, une fois les corrections passées.
  const remaining = Math.max(0, close.leftoverTotal - corrected)
  const versementEpargne =
    remaining > 0 && answers.leftover === 'savings' && close.savingsCategoryId !== null
  if (versementEpargne && close.savingsCategoryId) {
    const summary = await loadSummary(walletId, close.next.start)
    const savings = summary.envelopes.find((e) => e.category_id === close.savingsCategoryId)
    await setEnvelope({
      walletId,
      categoryId: close.savingsCategoryId,
      amount: (savings?.amount ?? 0) + remaining,
      cycleStart: close.next.start,
      carriesOver: true,
    })
  } else if (remaining > 0 && answers.leftover === 'spent') {
    await writeLocal('transactions', {
      id: await derivedId(`leftover:${walletId}:${close.range.start}`),
      wallet_id: walletId,
      category_id: null,
      entered_by_account_id: null,
      amount: remaining,
      direction: 'expense' as const,
      operation_date: close.range.end,
      title: 'Dépense non détaillée',
      note: null,
    })
  }
  // 'available' n'écrit RIEN : l'argent est toujours en poche, et l'enveloppe du
  // cycle écoulé cesse d'elle-même de le réserver. Inventer ici un revenu
  // « reste du mois dernier » compterait deux fois le même argent.

  // 3bis. Le nouveau cycle se pose exactement comme au premier lancement :
  //       le rythme peut changer (on passe d'un salaire mensuel à du commerce
  //       à la semaine), et le montant devient la nouvelle estimation.
  const wallet = await db.wallets.get(walletId)
  const cycle = answers.cycle ?? wallet?.cycle ?? 'monthly'

  if (wallet && (cycle !== wallet.cycle ||
      (answers.income != null && answers.income !== wallet.expected_income))) {
    await writeLocal('wallets', {
      id: wallet.id, name: wallet.name, cycle,
      expected_income: answers.income ?? wallet.expected_income ?? null,
      manual_category_order: wallet.manual_category_order ?? false,
    })
  }

  if (answers.income != null && answers.income > 0) {
    // L'argent d'ouverture d'un cycle donné : il n'y en a qu'un, quel que soit
    // l'appareil qui a fait le point. Le marqueur de clôture vit dans `meta`,
    // qui n'est PAS synchronisé — donc deux appareils peuvent parfaitement
    // solder le même cycle, et fabriquaient jusqu'ici deux revenus.
    const incomeId = await derivedId(`cycle-income:${walletId}:${close.next.start}`)
    // Le même rôle que le revenu du premier lancement : l'argent d'ouverture du
    // cycle. On le retient pour qu'une correction de réglage le REMPLACE au lieu
    // de s'y ajouter.
    await rememberCycleIncome(walletId, incomeId)
    await writeLocal('transactions', {
      id: incomeId,
      wallet_id: walletId,
      category_id: null,
      entered_by_account_id: null,
      amount: answers.income,
      direction: 'income' as const,
      // Dans le cycle QUI REDÉMARRE, et non « aujourd'hui » en dur. Les deux se
      // confondent le plus souvent, mais pas quand on solde un cycle depuis un
      // point du temps qui n'est pas le jour même : l'argent d'ouverture
      // tomberait alors hors du cycle qu'il ouvre.
      operation_date: contains(close.next, today()) ? today() : close.next.start,
      title: cycleIncomeLabel(cycle),
      note: null,
    })
  }

  // 5. Repartir de zéro : une enveloppe à 0 datée du nouveau cycle. Sans ça les
  //    montants du cycle écoulé se reconduisent, ce qui est le comportement par
  //    défaut — et le bon pour la plupart des gens.
  if (answers.budgets === 'reset') {
    // TOUTES les enveloppes du cycle écoulé, et non les seules qui gardaient un
    // reste : une enveloppe entièrement dépensée — ou dépassée — n'est pas dans
    // `leftovers` et reconduisait donc son montant en silence, ce qui est
    // exactement le contraire de ce que « repartir de zéro » promet.
    const solde = await loadSummary(walletId, close.range.start)
    for (const envelope of solde.envelopes) {
      // Sauf celle où l'utilisateur vient de verser son reste, deux écrans plus
      // tôt dans le même parcours : l'annuler dans la foulée serait absurde.
      if (versementEpargne && envelope.category_id === close.savingsCategoryId) continue
      await setEnvelope({
        walletId, categoryId: envelope.category_id, amount: 0,
        cycleStart: close.next.start,
        // Sans ces deux-là, l'enveloppe remise à zéro perdrait son mode de plan
        // et son cumul : `setEnvelope` n'a rien à reprendre sur un cycle neuf.
        planMode: envelope.plan_mode,
        carriesOver: envelope.carries_over,
      })
    }
  }

  // Marqueur local et non synchronisé : c'est une invite d'écran, pas une
  // donnée. Un second appareil pourra reposer la question, ce qui est bénin —
  // les corrections, elles, sont des transactions et voyagent normalement.
  await db.meta.put({ key: closeKey(close.range), value: new Date().toISOString() })
}

/**
 * Retire de l'argent d'une enveloppe cumulative.
 *
 * Écrit un REVENU dans la catégorie : l'argent quitte l'épargne et revient dans
 * la poche, donc dans l'argent à placer. C'est le seul mouvement possible depuis
 * une épargne, et c'est délibéré — on ne dépense pas « depuis l'épargne », on la
 * ramène d'abord parmi l'argent qu'on répartit. Sans ça, chaque dépense
 * demanderait à l'utilisateur de choisir sa source, ce qui est exactement la
 * complexité que le budget à enveloppes existe pour éviter.
 */
export async function withdrawFromSavings(
  walletId: string, categoryId: string, amount: number,
): Promise<void> {
  if (amount <= 0) return

  // Le retrait abaisse aussi la contribution du cycle en cours. Sans ça,
  // l'enveloppe se retrouverait « en retard » du montant retiré et regèlerait
  // aussitôt l'argent qu'on vient d'en sortir — l'utilisateur verrait son
  // épargne baisser sans que rien ne devienne disponible.
  const wallet = await db.wallets.get(walletId)
  const range = cycleRange(today(), wallet?.cycle ?? 'monthly')
  const summary = await loadSummary(walletId)
  const envelope = summary.envelopes.find((e) => e.category_id === categoryId)
  if (envelope) {
    await setEnvelope({
      walletId, categoryId,
      amount: Math.max(0, envelope.amount - amount),
      cycleStart: range.start,
      carriesOver: true,
    })
  }

  await writeLocal('transactions', {
    id: newId(),
    wallet_id: walletId,
    category_id: categoryId,
    entered_by_account_id: null,
    amount,
    direction: 'income' as const,
    operation_date: today(),
    title: 'Retrait de l’épargne',
    note: null,
  })
}

/**
 * Ce qu'une transaction doit à un plan d'enveloppe.
 *
 * Une enveloppe « jour par jour » produit des lignes rigoureusement
 * identiques — « École −3 333 Ar », neuf fois. Sans repère, l'historique
 * devient illisible et l'utilisateur ne sait plus laquelle il regarde.
 */
export interface PlannedTag {
  /** Le rang de la journée dans le plan du cycle, à partir de 1. */
  index: number
  /** Le nombre de journées prévues dans ce plan. */
  total: number
  mode: PlanMode
}

/**
 * Le plan d'origine de chaque transaction confirmée, indexé par transaction.
 *
 * On donne le RANG de la journée et non le compte de journées confirmées : ce
 * dernier est le même sur toutes les lignes du plan, il ne les distingue pas,
 * et il changerait rétroactivement à chaque nouvelle confirmation. Le rang, lui,
 * est propre à la ligne et ne bouge plus. Le compte reste à sa place, sur
 * l'enveloppe, où il décrit l'avancement.
 */
export async function plannedTags(walletId: string): Promise<Map<string, PlannedTag>> {
  const [wallet, allocations, budgets] = await Promise.all([
    db.wallets.get(walletId),
    db.budget_allocations.where({ wallet_id: walletId }).toArray(),
    db.budgets.where({ wallet_id: walletId }).toArray(),
  ])
  const cycle = wallet?.cycle ?? 'monthly'
  const kept_ = kept(allocations)

  // Un plan = une catégorie sur un cycle. Le rang se compte là-dedans, pas sur
  // tout l'historique : « journée 34 sur 9 » ne voudrait rien dire.
  const groups = new Map<string, BudgetAllocation[]>()
  for (const allocation of kept_) {
    const start = cycleRange(allocation.planned_date, cycle).start
    const key = `${allocation.category_id}|${start}`
    groups.set(key, (groups.get(key) ?? []).concat(allocation))
  }

  const tags = new Map<string, PlannedTag>()
  for (const [key, group] of groups) {
    const start = key.slice(key.indexOf('|') + 1)
    const categoryId = key.slice(0, key.indexOf('|'))
    const mode = applicableBudget(kept(budgets), categoryId, start)?.plan_mode ?? 'none'
    const ordered = [...group].sort((a, b) => a.planned_date.localeCompare(b.planned_date))

    ordered.forEach((allocation, i) => {
      if (!allocation.transaction_id) return
      tags.set(allocation.transaction_id, { index: i + 1, total: ordered.length, mode })
    })
  }
  return tags
}

/**
 * Supprimer une opération.
 *
 * Le tombstone habituel, plus un cas particulier : une dépense née d'une
 * journée planifiée porte une ALLOCATION qui la référence. La retirer sans
 * défaire la confirmation laisserait une journée marquée « faite » sans la
 * moindre dépense derrière — l'avancement du plan compterait une journée qui
 * n'existe plus. La journée redevient donc à confirmer, et le plan la
 * repropose : c'est la seule issue qui ne perd pas d'information.
 */
export async function deleteTransaction(
  walletId: string, transactionId: string,
): Promise<void> {
  const allocation = (await db.budget_allocations.where({ wallet_id: walletId }).toArray())
    .find((a) => a.transaction_id === transactionId && !a.deleted_at)

  if (allocation) {
    await writeLocal('budget_allocations', {
      ...allocation, confirmed_at: null, transaction_id: null,
    })
  }
  await deleteLocal('transactions', transactionId)
}
